import { call, put, select, takeLatest } from 'redux-saga/effects'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { getPcrIdSelector } from '../../home/selectors'
import { throwPcrException } from '../../pcr/store/pcr.actions'
import { IPcr, IAttachment } from '../../pcr/store/pcr.types'
import { getServiceIdSelector, getUserIdSelector } from '../../user/selectors'
import { getSignaturesSelector } from '../selectors/getSignatures.selector'
import { SignaturesConstants } from '../store/signatures.constants'
import Logger from '../../../services/logger.service'
import { IImageAttachment, ISignaturesState } from '../store/signatures.types'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { setSelectedSignatureType, setSignatureSection } from '../store/signatures.actions'
import { ISignatureOptionsState } from '../../options/store/options.types'
import { getSignatureOptionsSelector } from '../../options/selectors'
import { IOption } from '../../../common/common.types'
import { determineNextSignatureSection } from '../util/signatures.util'

export function* saveAttachment() {
  const serviceId: string = yield select(getServiceIdSelector)
  const pcrDocsService = new PcrDocsService(serviceId)
  const userId: string = yield select(getUserIdSelector)
  const pcrId: string = yield select(getPcrIdSelector)
  const signatureState: ISignaturesState = yield select(getSignaturesSelector)
  const { currentSignature, selectedSignatureType, chips, isMark } = signatureState
  const { signatureTypeOptions, sectionOptions }: ISignatureOptionsState = yield select(getSignatureOptionsSelector)

  if (!pcrId) {
    return
  }

  try {
    const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrId)
    Logger.info(`${selectedSignatureType} signature in PCR: ${pcrId} saved by user: ${userId} in service: ${serviceId}`)

    const reader = new FileReader()
    reader.readAsDataURL(currentSignature)
    reader.onloadend = function () {
      const signatureData = reader.result as string
      const attachment: IAttachment = {
        fileExtension: currentSignature.type,
        data: signatureData.substring(signatureData.indexOf(',') + 1),
        length: currentSignature.size,
      }

      let signatureType: string | null = null
      if (selectedSignatureType?.signatureType === SignaturesConstants.PATIENT) {
        signatureType = 'StandardSignatures_Section1_Patient'
      } else if (selectedSignatureType?.signatureType === SignaturesConstants.WITNESS) {
        signatureType = 'StandardSignatures_Section1_Witness'
      } else if (selectedSignatureType?.signatureType === SignaturesConstants.AMBULANCE) {
        signatureType = 'StandardSignatures_Section1_Ambulance'
      } else if (selectedSignatureType?.signatureType === SignaturesConstants.REPRESENTATIVE) {
        signatureType = 'StandardSignatures_Section2_Representative'
      } else if (selectedSignatureType?.signatureType === SignaturesConstants.CREW) {
        signatureType = 'StandardSignatures_Section3_Crew'
      } else if (selectedSignatureType?.signatureType === SignaturesConstants.RECEIVING) {
        signatureType = 'StandardSignatures_Section3_Receiving'
      }
      if (signatureType) {
        const newSignature = {
          key: signatureType,
          imageSrc: `data:${attachment.fileExtension};base64,${attachment.data}`,
        } as IImageAttachment

        const foundSignatureIndex = signatureState.attachments.findIndex((data) => data.key === signatureType)

        if (foundSignatureIndex !== -1) {
          signatureState.attachments[foundSignatureIndex] = newSignature
        } else {
          signatureState.attachments.push(newSignature)
        }

        const pcrDocUpdated: IPcr = {
          ...pcrDoc,
          signatures: { ...signatureState, selectedSignatureType: null },
          _attachments: { ...pcrDoc._attachments, [signatureType]: attachment },
        }
        pcrDocsService.savePCR(pcrDocUpdated)
      }
    }

    // If the user fills out one of the "crew" or "receiving" signature sections but not the other,
    // automatically take them to the other section, since the two must be completed in tandem.
    const newSignatureType = determineNextSignatureSection(selectedSignatureType, chips, signatureTypeOptions, {
      isMark: isMark,
    })
    if (newSignatureType) {
      const selectedSection = sectionOptions.find((section: IOption) => section.id === newSignatureType?.section)
      yield put(setSignatureSection(selectedSection))
    }
    yield put(setSelectedSignatureType(newSignatureType))
  } catch (e) {
    yield put(throwPcrException(e))
  }
}

export function* saveAttachmentSaga() {
  yield takeLatest([SignaturesConstants.SAVE_ATTACHMENT], performWithMutexHOG, saveAttachment)
}
